import { MsalProvider } from '@azure/msal-react'
import type { ReactNode } from 'react'

import { QueryProvider } from '@/app/providers/QueryProvider'
import { ThemeProvider } from '@/app/providers/ThemeProvider'
import { AppErrorBoundary } from '@/components/common/AppErrorBoundary'
import { Toaster } from '@/components/ui/sonner'
import { TooltipProvider } from '@/components/ui/tooltip'
import { useSyncAuthProfile } from '@/features/auth/hooks/use-auth'
import { msalInstance } from '@/lib/auth/msal'

/** Calls `useMsal()`, so it must only ever render inside an `MsalProvider`. */
function AuthProfileSync() {
  useSyncAuthProfile()
  return null
}

/** Passes children through untouched when auth is switched off. */
function MaybeMsalProvider({ children }: { children: ReactNode }) {
  if (!msalInstance) return children
  return (
    <MsalProvider instance={msalInstance}>
      <AuthProfileSync />
      {children}
    </MsalProvider>
  )
}

/** Every cross-cutting provider, in one place, outermost first. */
export function AppProviders({ children }: { children: ReactNode }) {
  return (
    <AppErrorBoundary>
      <MaybeMsalProvider>
        <ThemeProvider>
          <QueryProvider>
            <TooltipProvider delayDuration={200}>
              {children}
              <Toaster richColors closeButton />
            </TooltipProvider>
          </QueryProvider>
        </ThemeProvider>
      </MaybeMsalProvider>
    </AppErrorBoundary>
  )
}
